import { HexMark, Icon, SimulatedTag } from '@hive/ui';
import type { Screen } from '../api/client';
import { usePreview } from '../api/hooks';
import { cardState, heartbeatAge, screenHeadline, scoreline } from '../lib/format';

interface Props {
  screen: Screen;
  now: number;
  onChange(screen: Screen): void;
  onPair(screen: Screen): void;
  onTracks?(screen: Screen): void;
}

/** Live thumbnail with state overlays. Never a stock image: the real frame or a placeholder. */
function Thumb({ screen }: { screen: Screen }) {
  const url = usePreview(screen.id, screen.preview?.capturedAt);
  const state = cardState(screen);
  const np = screen.nowPlaying;
  const score = scoreline(np);
  return (
    <div className={`k-thumb k-thumb--${state}`}>
      {url && state !== 'offline' ? (
        <img src={url} alt={`${screen.name} screen, live preview`} className="k-thumb__img" />
      ) : (
        <span className="k-thumb__placeholder">
          {state === 'offline' ? 'No signal from player' : state === 'unpaired' ? 'Not paired' : 'Waiting for the first preview'}
        </span>
      )}
      <span className="k-thumb__top">
        {state === 'offline' ? (
          <span className="hv-pill hv-pill--offline k-thumb__pill">Offline · last heard {heartbeatAge(screen.device?.lastSeenAt, Date.now())}</span>
        ) : screen.promo ? (
          <span className="hv-pill hv-pill--overlay k-thumb__pill">Nectar slate</span>
        ) : np?.status === 'live' ? (
          <span className="hv-pill hv-pill--solid-live k-thumb__pill">
            <span className="k-dot-white" />
            Live{np.clock ? ` · ${np.clock}` : ''}
            {np.scoreSource === 'simulated' && np.clock && <SimulatedTag title="Match clock is simulated" />}
          </span>
        ) : np?.kind === 'loop' ? (
          <span className="hv-pill hv-pill--overlay k-thumb__pill">Sponsor loop</span>
        ) : np?.kind === 'channel' ? (
          <span className="hv-pill hv-pill--overlay k-thumb__pill">Channel</span>
        ) : null}
      </span>
      {score && state !== 'offline' && !screen.promo && (
        <span className="k-thumb__score">
          {score}
          {np?.scoreSource === 'simulated' && <SimulatedTag title="Score is simulated: no live data feed" />}
        </span>
      )}
    </div>
  );
}

export function ScreenCard({ screen, now, onChange, onPair, onTracks }: Props) {
  const state = cardState(screen);
  const { title, detail } = screenHeadline(screen);
  const tone = state === 'offline' ? 'red' : state === 'playing' ? 'teal' : state === 'unpaired' ? 'muted' : 'blue';
  const meta = [screen.code, screen.sizeInches ? `${screen.sizeInches} in` : null].filter(Boolean).join(' · ');
  return (
    <article className={`k-card k-screen k-screen--${state}`} aria-label={screen.name}>
      <Thumb screen={screen} />
      <div className="k-screen__row">
        <div className="k-screen__name">
          <HexMark size={32} tone={tone}>
            <Icon name={state === 'playing' ? 'play' : 'screen'} size={16} />
          </HexMark>
          <span>{screen.name}</span>
        </div>
        <span className="k-screen__meta">{meta}</span>
      </div>
      <div className="k-screen__what">
        <span className="k-screen__title">{title}</span>
        <span className="k-screen__detail">{detail}</span>
      </div>
      {screen.device && (
        <span className={`k-screen__beat${state === 'offline' ? ' k-screen__beat--late' : ''}`}>
          Heartbeat {heartbeatAge(screen.device.lastSeenAt, now)} · player {screen.device.hardwareId}
        </span>
      )}
      {state === 'unpaired' ? (
        <button type="button" className="hv-btn hv-btn--outline-accent hv-btn--block" onClick={() => onPair(screen)}>
          Pair a player
        </button>
      ) : (
        <div className="k-screen__actions">
          <button type="button" className="hv-btn hv-btn--outline-accent k-grow" onClick={() => onChange(screen)}>
            Change
          </button>
          {onTracks && (
            <button
              type="button"
              className="hv-btn hv-btn--secondary k-screen__tracks"
              aria-label={`Audio and subtitles for ${screen.name}`}
              title="Audio and subtitles"
              onClick={() => onTracks(screen)}
            >
              <Icon name="sound" size={18} />
              {screen.tracks?.preferences.subtitles ? 'CC' : ''}
            </button>
          )}
        </div>
      )}
    </article>
  );
}
